import { useEffect, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { ChevronRight, Loader2, Save, Trash2 } from "lucide-react"
import type { AppShell } from "@/App"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { confirmDialog } from "@/lib/confirm"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  fetchSettings,
  getSimulation,
  listBrands,
  listContractedPowers,
  paymentTermsValid,
  FALLBACK_PAYMENT_TERMS,
  type BrandRow,
  type ContractedPowerRow,
  type PaymentTermLine,
  type PaymentTermsPreset,
  type PhaseType,
  type SettingsRow,
  type TariffType,
  type SimulationStatus,
  listComerciais,
  setProposalOwner,
  type Comercial,
} from "@/lib/api"
import { PaymentTermsLines } from "@/components/PaymentTermsLines"
import { ConsumptionCard } from "@/components/ConsumptionCard"
import {
  averageMonths,
  estimateMonthlyKwh,
  periodPrices,
  periodTotal,
  toDistribution,
  type ConsumptionMonth,
  type PeriodKwh,
} from "@/lib/consumption"

/** Sentinela do dropdown para condições fora dos presets. */
const CUSTOM_TERMS = "__custom__"

/** Duas listas de linhas são o mesmo preset se pct+label baterem por ordem. */
function sameLines(a: PaymentTermLine[], b: PaymentTermLine[]): boolean {
  return (
    a.length === b.length &&
    a.every(
      (l, i) =>
        Math.abs(Number(l.pct) - Number(b[i].pct)) < 0.01 &&
        l.label.trim() === b[i].label.trim(),
    )
  )
}

interface EditProps {
  shell: AppShell | null
}

interface FormState {
  status: SimulationStatus
  client_name: string
  client_email: string
  client_phone: string
  address: string
  city: string
  postal_code: string
  latitude: string
  longitude: string
  house_type: string
  roof_material: string
  roof_inclination_type: string
  tariff_type: TariffType
  phase_type: PhaseType
  brand_id: string
  contracted_power_id: string
  has_invoice: boolean
  average_monthly_bill_eur: string
  has_charger: boolean
  has_meter: boolean
  has_battery: boolean
  has_backup: boolean
  notes: string
  conditions_notes: string
}

export function Edit({ shell }: EditProps) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [form, setForm] = useState<FormState | null>(null)
  const [number, setNumber] = useState<string>("")
  const [brands, setBrands] = useState<BrandRow[]>([])
  const [powers, setPowers] = useState<ContractedPowerRow[]>([])
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // ── Condições de pagamento ────────────────────────────────────────────
  const [presets, setPresets] = useState<PaymentTermsPreset[]>([])
  const [termsMode, setTermsMode] = useState<string>(CUSTOM_TERMS)
  const [termsLines, setTermsLines] = useState<PaymentTermLine[]>(
    FALLBACK_PAYMENT_TERMS,
  )
  // ── Consumos ──────────────────────────────────────────────────────────
  const [settingsRow, setSettingsRow] = useState<SettingsRow | null>(null)
  const [dist, setDist] = useState<PeriodKwh>({ empty: 0.3, peak: 0.4, full: 0.3 })
  const [months, setMonths] = useState<ConsumptionMonth[]>([])
  // Comercial associado (= proposals.created_by). Editável só por admins.
  const [comerciais, setComerciais] = useState<Comercial[]>([])
  const [ownerId, setOwnerId] = useState<string>("")
  const [originalOwner, setOriginalOwner] = useState<string>("")

  useEffect(() => {
    if (!shell || !id) return
    setLoading(true)
    Promise.all([
      getSimulation(shell, id),
      listBrands(shell),
      listContractedPowers(shell),
      fetchSettings(shell).catch(() => null),
    ])
      .then(([sim, brandRows, powerRows, settings]) => {
        const s = sim as unknown as Record<string, unknown>

        // Presets disponíveis + reconciliação com os termos guardados: se a
        // proposta bater certo com um preset, o dropdown mostra-o; caso
        // contrário (ou termos ausentes) cai em Personalizado / default.
        const availablePresets: PaymentTermsPreset[] =
          settings?.payment_terms_presets?.length
            ? settings.payment_terms_presets
            : [{ name: "30/70", lines: FALLBACK_PAYMENT_TERMS }]
        setPresets(availablePresets)

        const stored = Array.isArray(s.payment_terms_json)
          ? (s.payment_terms_json as PaymentTermLine[])
          : null
        const effective = stored?.length ? stored : availablePresets[0].lines
        setTermsLines(effective)
        const match = availablePresets.find((p) => sameLines(p.lines, effective))
        setTermsMode(match ? match.name : CUSTOM_TERMS)

        // Consumos: distribuição guardada (ou defaults das settings) + meses reais.
        setSettingsRow(settings)
        setDist({
          empty: Number(
            s.consumption_distribution_empty ??
              settings?.default_consumption_empty_fraction ??
              0.3,
          ),
          peak: Number(
            s.consumption_distribution_peak ??
              settings?.default_consumption_peak_fraction ??
              0.4,
          ),
          full: Number(
            s.consumption_distribution_full ??
              settings?.default_consumption_full_fraction ??
              0.3,
          ),
        })
        setMonths(
          Array.isArray(s.consumption_months_json)
            ? (s.consumption_months_json as ConsumptionMonth[])
            : [],
        )
        setForm({
          status: (s.status as SimulationStatus) ?? "draft",
          client_name: (s.client_name as string) ?? "",
          client_email: (s.client_email as string | null) ?? "",
          client_phone: (s.client_phone as string | null) ?? "",
          address: (s.address as string | null) ?? "",
          city: (s.city as string | null) ?? "",
          postal_code: (s.postal_code as string | null) ?? "",
          latitude: s.latitude != null ? String(s.latitude) : "",
          longitude: s.longitude != null ? String(s.longitude) : "",
          house_type: (s.house_type as string | null) ?? "moradia",
          roof_material: (s.roof_material as string | null) ?? "telha",
          // Normaliza dados antigos (baixa/alta) para "inclinado".
          roof_inclination_type:
            s.roof_inclination_type === "plano" ? "plano" : "inclinado",
          tariff_type: (s.tariff_type as TariffType) ?? "simples",
          phase_type: (s.phase_type as PhaseType) ?? "monofasico",
          brand_id: s.brand_id != null ? String(s.brand_id) : "",
          contracted_power_id: (s.contracted_power_id as string) ?? "",
          has_invoice: Boolean(s.has_invoice),
          average_monthly_bill_eur:
            s.average_monthly_bill_eur != null
              ? String(s.average_monthly_bill_eur)
              : "",
          has_charger: Boolean(s.has_charger),
          has_meter: Boolean(s.has_meter),
          has_battery: Boolean(s.has_battery),
          has_backup: Boolean(s.has_backup),
          notes: (s.notes as string | null) ?? "",
          conditions_notes: (s.conditions_notes as string | null) ?? "",
        })
        setNumber((s.number as string) ?? "")
        const owner = (s.created_by as string | null) ?? ""
        setOwnerId(owner)
        setOriginalOwner(owner)
        setBrands(brandRows.filter((b) => b.is_active))
        setPowers(powerRows.filter((p) => p.is_active))
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Falha a carregar"),
      )
      .finally(() => setLoading(false))
  }, [shell, id])

  useEffect(() => {
    if (!shell?.isAdmin) return
    listComerciais(shell)
      .then(setComerciais)
      .catch(() => setComerciais([]))
  }, [shell])

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f))
  }

  async function save() {
    if (!shell || !form || !id) return
    // Gate: condições de pagamento têm de somar 100% e ter descrição.
    if (!paymentTermsValid(termsLines) || termsLines.some((l) => !l.label.trim())) {
      shell.bridge.toast({
        variant: "error",
        description:
          "Condições de pagamento: percentagens têm de somar 100% e cada linha precisa de descrição",
      })
      return
    }

    // ── Consumos: deriva total mensal + distribuição do card ──────────────
    // SIM (tem fatura) → média dos meses reais. NÃO → estimativa pelo valor.
    // Em ambos os casos alimenta monthly_consumption_total_kwh + distribuição,
    // que é o que o motor usa para o gráfico e o dimensionamento.
    let perPeriod: PeriodKwh
    let monthsPayload: ConsumptionMonth[] | null = null
    if (form.has_invoice) {
      const filled = months.filter(
        (m) => Number(m.empty) + Number(m.peak) + Number(m.full) > 0,
      )
      if (filled.length < 3) {
        shell.bridge.toast({
          variant: "error",
          description: "Com fatura: preenche pelo menos 3 meses de consumo",
        })
        return
      }
      perPeriod = averageMonths(months)
      monthsPayload = months
    } else {
      const prices = settingsRow
        ? periodPrices(settingsRow, form.tariff_type)
        : { empty: 0, peak: 0, full: 0 }
      const fixedFrac = Number(settingsRow?.estimate_fixed_cost_fraction ?? 0.26)
      perPeriod = estimateMonthlyKwh(
        Number(form.average_monthly_bill_eur) || 0,
        fixedFrac,
        dist,
        prices,
      )
    }
    const monthlyTotalKwh = periodTotal(perPeriod)
    if (monthlyTotalKwh <= 0) {
      shell.bridge.toast({
        variant: "error",
        description: "Consumo inválido — verifica o valor da conta ou os meses",
      })
      return
    }
    const distOut = toDistribution(perPeriod)

    setSaving(true)
    try {
      const res = await shell.client.post<{ success: boolean; error?: string }>(
        "actions/solar_update",
        {
          params: {
            id,
            // Condições de pagamento desta proposta (não força recálculo).
            payment_terms_json: termsLines,
            // Consumos derivados do card (força recálculo do dimensionamento).
            monthly_consumption_total_kwh: monthlyTotalKwh,
            consumption_distribution_empty: distOut.empty,
            consumption_distribution_peak: distOut.peak,
            consumption_distribution_full: distOut.full,
            consumption_months_json: monthsPayload,
            client_name: form.client_name,
            client_email: form.client_email || null,
            client_phone: form.client_phone || null,
            address: form.address || null,
            city: form.city || null,
            postal_code: form.postal_code || null,
            latitude: Number(form.latitude),
            longitude: Number(form.longitude),
            house_type: form.house_type,
            roof_material: form.roof_material,
            roof_inclination_type: form.roof_inclination_type,
            tariff_type: form.tariff_type,
            phase_type: form.phase_type,
            brand_id: Number(form.brand_id),
            contracted_power_id: form.contracted_power_id,
            has_invoice: form.has_invoice,
            average_monthly_bill_eur: Number(form.average_monthly_bill_eur),
            has_charger: form.has_charger,
            has_meter: form.has_meter,
            has_battery: form.has_battery,
            has_backup: form.has_backup,
            notes: form.notes || null,
            conditions_notes: form.conditions_notes || null,
          },
        },
      )
      if (!res.success) throw new Error(res.error)
      // Reatribuição do comercial (endpoint próprio, só admin + auditado).
      if (shell.isAdmin && ownerId && ownerId !== originalOwner) {
        await setProposalOwner(shell, id, ownerId)
      }
      // Mudou estado?
      if (form.status) {
        await shell.client.post<{ success: boolean; error?: string }>(
          "actions/solar_change_status",
          { params: { id, status: form.status } },
        )
      }
      shell.bridge.toast({
        variant: "success",
        description: "Alterações guardadas",
      })
      navigate(`/solar/${id}`)
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a guardar",
      })
    } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (!shell || !id) return
    if (!(await confirmDialog("Eliminar esta simulação?", { destructive: true, confirmLabel: "Eliminar" }))) return
    try {
      await shell.client.post("actions/solar_delete", { params: { id } })
      shell.bridge.toast({ variant: "success", description: "Eliminada" })
      navigate("/list")
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha",
      })
    }
  }

  if (loading) return <p className="text-sm text-muted-foreground">A carregar…</p>
  if (error) return <p className="text-sm text-destructive">{error}</p>
  if (!form) return null

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <nav className="flex items-center gap-1 text-xs text-muted-foreground">
            <Link to="/list" className="hover:underline">
              Simulações
            </Link>
            <ChevronRight className="h-3 w-3" />
            <Link to={`/solar/${id}`} className="hover:underline">
              {number}
            </Link>
            <ChevronRight className="h-3 w-3" />
            <span>Editar</span>
          </nav>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            Editar {number}
          </h1>
        </div>
        <Button variant="destructive" onClick={remove}>
          <Trash2 className="h-4 w-4" />
          Eliminar
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Informação Básica</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Número</Label>
              <Input value={number} disabled />
            </div>
            <div className="space-y-1">
              <Label required>Estado</Label>
              <Select
                value={form.status}
                onValueChange={(v) => update("status", v as SimulationStatus)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="under_review">Em revisão</SelectItem>
                  <SelectItem value="sent">Enviada</SelectItem>
                  {/* «Ganho» só pelo «Fechar negócio» no detalhe (relatório, fatura, fotos). */}
                  {form.status === "ganho" && <SelectItem value="ganho">Ganho</SelectItem>}
                  <SelectItem value="perdido">Perdido</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Comercial associado — reatribuir (ex.: do Mauro para o Henrique).
                Só admins: muda a capa do PDF, a visibilidade e o vendedor no Odoo. */}
            {shell?.isAdmin && (
              <div className="col-span-2 space-y-1">
                <Label>Comercial</Label>
                <Select value={ownerId} onValueChange={setOwnerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecionar comercial…" />
                  </SelectTrigger>
                  <SelectContent>
                    {ownerId && !comerciais.some((c) => c.id === ownerId) && (
                      <SelectItem value={ownerId}>Comercial atual (sem conta ativa)</SelectItem>
                    )}
                    {comerciais.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name || c.email || c.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ownerId !== originalOwner && (
                  <p className="text-xs text-amber-600">
                    Ao guardar, a proposta passa para este comercial (capa do PDF,
                    lista e vendedor no Odoo).
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Cliente</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <FormField label="Nome" required>
              <Input
                value={form.client_name}
                onChange={(e) => update("client_name", e.target.value)}
              />
            </FormField>
            <FormField label="Email">
              <Input
                type="email"
                value={form.client_email}
                onChange={(e) => update("client_email", e.target.value)}
              />
            </FormField>
            <FormField label="Telefone">
              <Input
                value={form.client_phone}
                onChange={(e) => update("client_phone", e.target.value)}
              />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Localização</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <FormField label="Morada" required>
              <Input
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
              />
            </FormField>
            <FormField label="Cidade" required>
              <Input
                value={form.city}
                onChange={(e) => update("city", e.target.value)}
              />
            </FormField>
            <FormField label="Código postal" required>
              <Input
                value={form.postal_code}
                onChange={(e) => update("postal_code", e.target.value)}
              />
            </FormField>
            <FormField label="Latitude" required>
              <Input
                type="number"
                step="0.000001"
                value={form.latitude}
                onChange={(e) => update("latitude", e.target.value)}
              />
            </FormField>
            <FormField label="Longitude" required>
              <Input
                type="number"
                step="0.000001"
                value={form.longitude}
                onChange={(e) => update("longitude", e.target.value)}
              />
            </FormField>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Imóvel</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <FormField label="Tipo de casa" required>
              <Select
                value={form.house_type}
                onValueChange={(v) => update("house_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="moradia">Moradia</SelectItem>
                  <SelectItem value="apartamento">Apartamento</SelectItem>
                  <SelectItem value="comercio">Comércio</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Material do telhado" required>
              <Select
                value={form.roof_material}
                onValueChange={(v) => update("roof_material", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="telha">Telha</SelectItem>
                  <SelectItem value="telha_marselha">Telha marselha</SelectItem>
                  <SelectItem value="chapa">Chapa</SelectItem>
                  <SelectItem value="laje">Laje</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Inclinação do telhado" required>
              <Select
                value={form.roof_inclination_type}
                onValueChange={(v) => update("roof_inclination_type", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="plano">Plano (estrutura triangular)</SelectItem>
                  <SelectItem value="inclinado">Inclinado (estrutura coplanar)</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Energia e Consumo</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
            <FormField label="Fase" required>
              <Select
                value={form.phase_type}
                onValueChange={(v) => update("phase_type", v as PhaseType)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monofasico">Monofásico</SelectItem>
                  <SelectItem value="trifasico">Trifásico</SelectItem>
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Marca do Kit" required>
              <Select
                value={form.brand_id}
                onValueChange={(v) => update("brand_id", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {brands.map((b) => (
                    <SelectItem key={b.brand_id} value={String(b.brand_id)}>
                      {b.brand_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
            <FormField label="Potência contratada" required>
              <Select
                value={form.contracted_power_id}
                onValueChange={(v) => update("contracted_power_id", v)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {powers.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <SwitchField
              label="Incluir carregador"
              value={form.has_charger}
              onChange={(v) => update("has_charger", v)}
            />
            <SwitchField
              label="Incluir contador"
              value={form.has_meter}
              onChange={(v) => update("has_meter", v)}
            />
            <SwitchField
              label="Incluir bateria"
              value={form.has_battery || form.has_backup}
              disabled={form.has_backup}
              hint={
                form.has_backup
                  ? "Incluída automaticamente com o backup"
                  : "Armazenamento / auto-consumo"
              }
              onChange={(v) => update("has_battery", v)}
            />
            <SwitchField
              label="Incluir backup"
              value={form.has_backup}
              hint="Corte de rede — requer bateria"
              onChange={(v) => {
                update("has_backup", v)
                if (v) update("has_battery", true)
              }}
            />
          </div>
        </CardContent>
      </Card>

      <ConsumptionCard
        settings={settingsRow}
        tariffType={form.tariff_type}
        onTariff={(v) => update("tariff_type", v)}
        hasInvoice={form.has_invoice}
        onHasInvoice={(v) => update("has_invoice", v)}
        billEur={form.average_monthly_bill_eur}
        onBill={(v) => update("average_monthly_bill_eur", v)}
        dist={dist}
        onDist={setDist}
        months={months}
        onMonths={setMonths}
      />

      <Card>
        <CardHeader>
          <CardTitle>Condições de Pagamento</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <FormField label="Condições">
            <Select
              value={termsMode}
              onValueChange={(v) => {
                setTermsMode(v)
                // Escolher um preset copia as suas linhas; Personalizado
                // mantém as actuais como ponto de partida editável.
                const preset = presets.find((p) => p.name === v)
                if (preset) setTermsLines(preset.lines)
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {presets.map((p) => (
                  <SelectItem key={p.name} value={p.name}>
                    {p.name}
                  </SelectItem>
                ))}
                <SelectItem value={CUSTOM_TERMS}>Personalizado</SelectItem>
              </SelectContent>
            </Select>
          </FormField>

          {termsMode === CUSTOM_TERMS ? (
            <PaymentTermsLines lines={termsLines} onChange={setTermsLines} />
          ) : (
            <div className="space-y-1 text-sm">
              {termsLines.map((l, i) => (
                <div key={i} className="flex justify-between text-muted-foreground">
                  <span>{l.label}</span>
                  <span className="font-medium">{l.pct}%</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notas</CardTitle>
        </CardHeader>
        <CardContent>
          <textarea
            className="h-24 w-full rounded-md border border-input bg-background p-2 text-sm"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Notas — Condições Gerais</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="mb-2 text-xs text-muted-foreground">
            Texto opcional que aparece no PDF a seguir às condições gerais
            (definidas nas Configurações).
          </p>
          <textarea
            className="h-24 w-full rounded-md border border-input bg-background p-2 text-sm"
            value={form.conditions_notes}
            onChange={(e) => update("conditions_notes", e.target.value)}
          />
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate(`/solar/${id}`)}>
          Cancelar
        </Button>
        <Button onClick={save} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "A guardar…" : "Guardar alterações"}
        </Button>
      </div>
    </div>
  )
}

function FormField({
  label,
  required,
  children,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1">
      <Label required={required}>{label}</Label>
      {children}
    </div>
  )
}

function SwitchField({
  label,
  value,
  onChange,
  disabled = false,
  hint,
}: {
  label: string
  value: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  hint?: string
}) {
  return (
    <label className="flex items-center justify-between rounded-md border border-border p-3">
      <span className="flex flex-col">
        <span className="text-sm font-medium">{label}</span>
        {hint ? (
          <span className="text-xs text-muted-foreground">{hint}</span>
        ) : null}
      </span>
      <Switch checked={value} onCheckedChange={onChange} disabled={disabled} />
    </label>
  )
}
